/* The inbox's Send saves loaded draft edits before queueing; the queue transaction records approval.
 * Legacy Approve callers reuse the same save, then issue their own approval command.
 * The composer holds the whole email. A stored ending left intact is cut off the body before saving;
 * an edited ending stays in the body and clears the stored one, so nothing is appended twice.
 */
import { z } from "zod";
import {
  type PlanStep,
  type WorkPlan,
  WorkPlanSchema,
} from "../../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import type { DraftSendReference } from "../../src/inbox-v2/draft-send-contract.ts";
import { type DraftRevision, draftEmail } from "../../src/mail-content.ts";
import type { SignOffMode } from "../../src/sign-off-mode.ts";
import {
  type BoxContent,
  editorHtml,
  holdsOtherThan,
  paragraphs,
} from "../inbox-v2/reply-box-link.ts";
import { request } from "./page-request.ts";

const SavedSchema = z.object({ plan: WorkPlanSchema });

/**
 * What the box holds against the draft on the card: nothing else (empty, or the draft as it stands,
 * compared in the box's format so a bold alone counts); this draft, edited, which Approve saves;
 * words of the rep's own, written from scratch; or an edit of an earlier draft revision. Only an
 * edit of this draft is saved.
 */
export type BoxRelation = "none" | "edits" | "own" | "earlier";

export function boxRelation(box: BoxContent | null, draft: DraftRevision): BoxRelation {
  if (box === null) return "none";
  // Files the rep added to this draft or took off it are an edit of it, even with the words as they were.
  const files =
    box.text.trim() !== "" && box.loaded === draft.revision && box.files?.edited === true;
  if (!holdsOtherThan(box, draftEmail(draft)) && !files) return "none";
  if (box.loaded === null) return "own";
  return box.loaded === draft.revision ? "edits" : "earlier";
}

/**
 * The body to save from the box, and whether the draft's ending still closes the box's words. With
 * no ending of its own (none chosen, or a revision older than endings stored apart), the body is
 * everything the box holds.
 */
function boxBody(
  content: BoxContent,
  draft: DraftRevision,
  parse: (html: string) => Document,
): { html: string; endingKept: boolean } {
  const ending = draft.signOff?.plainText.trim() ? draft.signOff.plainText : null;
  if (ending === null) return { html: content.html, endingKept: true };
  if (content.format === "text") {
    const blocks = content.text.split(/\n{2,}/u);
    const tail = ending.split(/\n{2,}/u);
    const kept = blocks.length > tail.length && blocks.slice(-tail.length).join("\n\n") === ending;
    return kept
      ? { html: paragraphs(blocks.slice(0, -tail.length).join("\n\n")), endingKept: true }
      : { html: content.html, endingKept: false };
  }
  const blocks = [...parse(content.html).body.children];
  const normalizedEnding = editorHtml(draft.signOff?.html ?? "");
  for (let cut = blocks.length - 1; cut >= 0; cut -= 1) {
    const tail = blocks
      .slice(cut)
      .map((block) => block.outerHTML)
      .join("");
    if (editorHtml(tail) === normalizedEnding)
      return {
        html: blocks
          .slice(0, cut)
          .map((block) => block.outerHTML)
          .join(""),
        endingKept: true,
      };
  }
  return { html: content.html, endingKept: false };
}

/** Whether the draft's stored ending still closes the box's words, so another can replace it. */
export function endingIntact(
  content: BoxContent,
  draft: DraftRevision,
  parse: (html: string) => Document,
): boolean {
  return draft.signOff !== null && boxBody(content, draft, parse).endingKept;
}

/**
 * Saves the box's words with another saved ending, in one draft request and one revision. The draft
 * revision the server answered with; a refusal reaches the rep in the page's own words.
 */
export async function saveEnding(change: {
  planApi: string;
  plan: WorkPlan;
  step: PlanStep;
  draft: DraftRevision;
  box: BoxContent;
  mode: SignOffMode;
  parse: (html: string) => Document;
}): Promise<number> {
  const { box, draft } = change;
  if (box.loaded !== draft.revision || !endingIntact(box, draft, change.parse))
    throw new Error("The reply box no longer holds this draft's ending. Change it there by hand.");
  // A box holding the draft as it stands saves the draft's own body: Plain text has no links or
  // formatting to give back, and the rep changed only the ending.
  const html =
    boxRelation(box, draft) === "none" ? draft.html : boxBody(box, draft, change.parse).html;
  const written = await save(`${change.planApi}/draft`, {
    expectedRevision: change.plan.revision,
    stepId: change.step.id,
    html,
    ending: { mode: change.mode },
  });
  if (written.draftRevision === null) throw new Error("The draft did not change.");
  return written.draftRevision;
}

type Approval = {
  planApi: string;
  plan: WorkPlan;
  step: PlanStep;
  draft: DraftRevision;
  box: BoxContent | null;
  parse: (html: string) => Document;
  /** A successful save is acknowledged even if approval is subsequently refused. */
  onSaved: (revision: number) => void;
  exactFormatted?: boolean;
};

/** One write against the revision the last one produced; the server's answer is the next revision. */
async function write(path: string, body: StepWrite): Promise<number> {
  return (await save(path, body)).revision;
}

type StepWrite = {
  readonly stepId: string;
  readonly expectedRevision: number;
  readonly [field: string]: unknown;
};

/** A write's answer: the plan's next revision, and the draft revision its reply step now holds. */
async function save(path: string, body: StepWrite) {
  const { plan } = SavedSchema.parse(
    await request(path, { commandId: crypto.randomUUID(), ...body }),
  );
  const reply = plan.steps.find((step) => step.id === body.stepId)?.input;
  return {
    revision: plan.revision,
    draftRevision: reply?.kind === "reply" ? reply.draftRevision : null,
  };
}

/**
 * How many of the reply box's files Approve will not take: those in a box not working on this draft
 * as an edit (not loaded from it, or holding no words). The draft's own files, loaded with it and
 * unchanged, are the draft's, so none of them is left behind.
 */
export function filesLeftBehind(box: BoxContent | null, draft: DraftRevision): number {
  const files = box?.files;
  if (!box || !files || files.fileIds.length === 0) return 0;
  if (boxRelation(box, draft) === "edits") return 0;
  return box.loaded === draft.revision && !files.edited ? 0 : files.fileIds.length;
}

/** Said under Approve when it leaves the box's files behind, with the way to send them with the draft. */
export function filesLeftBehindNote(count: number): string {
  if (count === 0) return "";
  const them = count === 1 ? "it" : "them";
  return `Your reply box holds ${count === 1 ? "1 file" : `${count} files`}; Approve approves the agent's draft without ${them}. Use Edit in reply box first to send ${them} with the draft.`;
}

/**
 * Saves the box as the draft where it differs from it, then approves what was saved. The draft
 * revision the box's words became, or null where the draft was approved as it stood.
 */
export async function approveFromBox(approval: Approval): Promise<number | null> {
  const prepared = await saveBoxDraft(approval);
  await write(`${approval.planApi}/commands`, {
    expectedRevision: prepared.expectedRevision,
    stepId: approval.step.id,
    action: "approve",
  });
  return prepared.draftRevision === approval.draft.revision ? null : prepared.draftRevision;
}

/** Saves an edit before Send; approval belongs to the server's send transaction. */
export async function saveBoxDraft(approval: Approval): Promise<DraftSendReference> {
  const { planApi, plan, step, draft, box } = approval;
  let revision = plan.revision;
  let saved: number | null = null;
  // A box working on this draft with a file still going up, or one that did not, would approve the
  // draft without it: checked before whether the words changed, since a file alone may be in flight.
  if (box?.loaded === draft.revision && box.files && !box.files.ready)
    throw new Error(
      "A file in the reply box is still uploading or did not upload. Finish it there, then approve.",
    );
  if (
    box &&
    (boxRelation(box, draft) === "edits" || (approval.exactFormatted && box.format === "html"))
  ) {
    const body = boxBody(box, draft, approval.parse);
    const written = await save(`${planApi}/draft`, {
      expectedRevision: revision,
      stepId: step.id,
      html: body.html,
      ending: body.endingKept ? "keep" : "in_body",
      // The box's files go onto the saved revision; an empty list takes the draft's off.
      ...(box.files
        ? {
            files: {
              composerId: box.files.composerId,
              revision: box.files.revision,
              fileIds: [...box.files.fileIds],
            },
          }
        : {}),
    });
    revision = written.revision;
    saved = written.draftRevision;
    if (saved !== null) approval.onSaved(saved);
  }
  return {
    expectedRevision: revision,
    stepId: step.id,
    draftRevision: saved ?? draft.revision,
  };
}
